"use client";

import { SpringRef, Lookup } from "@react-spring/web";
import { createContext, useContext, ReactNode, useMemo } from "react";

import useQuestionLayoutHook, { ScrollRefItem } from "../question-layout.hook";

export interface ScrollOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
}

export interface QuestionLayoutContextType {
  formAnimationRef: SpringRef<Lookup>;
  footerAnimationRef: SpringRef<Lookup>;
  descriptionContentSpringRef: SpringRef<Lookup>;
  descriptionRef: React.RefObject<HTMLDivElement | null>;
  editPage: boolean;
  footerRef: React.RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  isCancelEditModalOpen: boolean;
  dynamicFooter: boolean;
  setIsCancelEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleCancelEditModalConfirmBtnClick: () => void;
  scrollToFallback: (options?: ScrollOptions, delay?: number) => void;
}

const QuestionLayoutContext = createContext<QuestionLayoutContextType | null>(
  null
);

export const useQuestionLayoutContext = () => {
  const context = useContext(QuestionLayoutContext);
  if (!context) {
    throw new Error(
      "useQuestionLayoutContext must be used within QuestionLayoutProvider"
    );
  }
  return context;
};

interface QuestionLayoutProviderProps {
  children: ReactNode;

  scrollRefs?: ScrollRefItem[];
}

export function QuestionLayoutProvider({
  children,
  scrollRefs = [],
}: QuestionLayoutProviderProps) {
  const {
    formAnimationRef,
    footerAnimationRef,
    descriptionContentSpringRef,
    descriptionRef,
    editPage,
    footerRef,
    isLoading,
    isCancelEditModalOpen,
    dynamicFooter,
    setIsCancelEditModalOpen,
    handleCancelEditModalConfirmBtnClick,
    scrollToFallback,
  } = useQuestionLayoutHook({ scrollRefs });

  const contextValue: QuestionLayoutContextType = useMemo(
    () => ({
      formAnimationRef,
      footerAnimationRef,
      descriptionContentSpringRef,
      descriptionRef,
      editPage,
      footerRef,
      isLoading,
      isCancelEditModalOpen,
      dynamicFooter,
      setIsCancelEditModalOpen,
      handleCancelEditModalConfirmBtnClick,
      scrollToFallback,
    }),
    [
      formAnimationRef,
      footerAnimationRef,
      descriptionContentSpringRef,
      descriptionRef,
      editPage,
      footerRef,
      isLoading,
      isCancelEditModalOpen,
      dynamicFooter,
      setIsCancelEditModalOpen,
      handleCancelEditModalConfirmBtnClick,
      scrollToFallback,
    ]
  );

  return (
    <QuestionLayoutContext.Provider value={contextValue}>
      {children}
    </QuestionLayoutContext.Provider>
  );
}
